import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core'
import { Router } from '@angular/router'
import { LanguageService, RoutingService } from '@spartacus/core'
import { TableStructure } from '@spartacus/storefront'
import { combineLatest, Observable, of, Subscription } from 'rxjs'
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators'
import { EbUnitSelectorService } from 'src/app/components/unit-selector/eb-unit-selector.service'
import { EbAnalyticsService } from "../../../analytics/services/facades/eb-analytics.service";
import { EbBaseSiteService } from '../../../site-context/services/eb-base-site.service'
import {
  EbProduct,
  EbProductTypeEnum,
  EbSortingVariants,
  EbVariantSearchPage
} from '../../interfaces/eb-product.interface'
import { EbCurrentProductService } from '../../services/facades/eb-current-product.service'
import { EbFacetService } from '../product-facet-navigation/facet/eb-facet.service'

declare type SelectedValues = { [key: string]: string }

@Component({
    selector: 'eb-product-variants-table',
    templateUrl: './eb-product-variants-table.component.html',
    styleUrls: ['./eb-product-variants-table.component.scss'],
    standalone: false
})
export class EbProductVariantsTableComponent implements OnInit, OnDestroy {
  get facets() {
    return this.searchResult?.facets
  }

  get ebProductTypeEnum() {
    return EbProductTypeEnum
  }

  pagedVariants: EbProduct[] = []
  pagedVariants$: Observable<EbProduct[]>
  pageSize = 20
  currentPage = -1
  totalResults: number
  freeTextSearch: string
  productVariantFilterHeight: number
  loading = false
  private readonly subscriptions = new Subscription()
  dataStructure$: Observable<TableStructure>
  searchResult: EbVariantSearchPage
  currentSelectedValues: SelectedValues
  currentProduct$ = this.ebCurrentProductService.getProduct() as Observable<EbProduct>

  private readonly selectedValues$ = this.routingService.getRouterState().pipe(
    distinctUntilChanged((x, y) => {
      // router emits new value also when the anticipated `nextState` changes
      // but we want to perform search only when current url changes
      return x.state.url === y.state.url
    }),
    map(routerState => {
      const { query } = this.facetService.getLinkParams(
        this.router.parseUrl(routerState.state.url)?.queryParams?.query ||
          this.router.parseUrl(routerState.state.url)?.queryParams?.filters ||
          ''
      )
      this.freeTextSearch = this.router.parseUrl(routerState.state.url)?.queryParams?.searchTerm
      const parts = query.split(/:/)
      const selectedValues = {}
      while (parts.length >= 2) {
        const value = parts.pop()
        const key = parts.pop()
        if (value && this.filterQueryByFacetCondition(key)) {
          selectedValues[key] = value
        }
      }
      this.firstPage(selectedValues)
      return selectedValues
    })
  )

  constructor(
    private readonly ebCurrentProductService: EbCurrentProductService,
    protected router: Router,
    protected cd: ChangeDetectorRef,
    protected facetService: EbFacetService,
    protected languageService: LanguageService,
    protected ebBaseSiteService: EbBaseSiteService,
    protected routingService: RoutingService,
    private readonly elementRef: ElementRef,
    protected ebUnitSelectorService: EbUnitSelectorService,
    protected ebAnalyticsService:EbAnalyticsService
  ) {
  }

  @HostListener('window:resize')
  onResize() {
    this.setTHeadTopAttribute()
  }

  @HostListener('window:scroll')
  scrollHandler() {
    if (!this.productVariantFilterHeight) {
      this.setTHeadTopAttribute()
    }
  }

  @HostListener('click', ['$event'])
  onSelectFilter(event: any) {
    if (event.path?.find(entry => entry.tagName === 'EB-PRODUCT-VARIANTS-FILTER')) {
      this.setTHeadTopAttribute()
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      combineLatest([
        this.selectedValues$,
        this.currentProduct$,
        this.languageService.getActive(),
        this.ebBaseSiteService.getActive(),
      ])
        .pipe(
          tap(() => (this.pagedVariants = [])),
          distinctUntilChanged((oldValue, newValue) => {
            return (
              JSON.stringify(oldValue[0]) === JSON.stringify(newValue[0]) &&
              oldValue[1].code === newValue[1].code &&
              oldValue[2] === newValue[2] &&
              oldValue[3] === newValue[3]
            )
          }),
          filter(([, product]) => this.isParent(product)),
          map(([selectedValues, ,]) => selectedValues)
        )
        .subscribe(selectedValues => {
          this.firstPage(selectedValues)
        })
    )
    this.fetchVariantsOnUnitChange()
    this.invokeMatomoEvents();
  }

  private isParent(product:EbProduct) {
    return product.ebProductType === this.ebProductTypeEnum.PARENT;
  }

  firstPage(selectedValues: SelectedValues) {
    this.pagedVariants = []
    this.currentPage = 0
    this.currentSelectedValues = selectedValues
    this.fetchVariants(this.ebUnitSelectorService.getUnitValue(),true, selectedValues)
  }

  nextPage() {
    this.currentPage++
    this.fetchVariants(this.ebUnitSelectorService.getUnitValue())
  }

  fetchVariants(unitValue, firstPage = false, selectedValues?) {
    this.pagedVariants$ = this.ebCurrentProductService
      .getVariantsForTable(
        unitValue,
        this.pageSize,
        this.currentPage,
        EbSortingVariants.NAMEASC,
        selectedValues ?? this.currentSelectedValues,
        this.freeTextSearch
      )
      .pipe(
        tap(() => (this.loading = true)),
        take(1),
        switchMap(variants => {
          if (variants?.pagination?.totalPages >= variants?.pagination?.currentPage) {
            this.totalResults = variants.pagination.totalResults
          }
          if (this.dataStructure$ === undefined) {
            this.dataStructure$ = this.ebCurrentProductService.getVariantsTableStructure(variants)
          }
          if (variants?.products) {
            this.pagedVariants = firstPage
              ? (variants.products as EbProduct[])
              : [...this.pagedVariants, ...(variants.products as EbProduct[])]
          }
          this.searchResult = {
            ...variants,
            products: this.pagedVariants
          }
          this.loading = false
          return of(this.pagedVariants)
        }),
        tap(() => this.cd.detectChanges())
      )
  }

  setTHeadTopAttribute() {
    this.productVariantFilterHeight = this.elementRef.nativeElement?.querySelector(
      'eb-product-variants-filter'
    )?.offsetHeight
    const thead = this.elementRef.nativeElement?.querySelector('thead')
    thead?.setAttribute('style', `top: ${this.productVariantFilterHeight}px`)
  }

  filterQueryByFacetCondition(code: string): boolean {
    const facets = this.facets
    if (facets?.length) {
      return !!facets.find(facet => facet.code === code)
    }
    return true
  }

  launchItem(event: any): void {
    this.routingService.go('/' + event.fullProductPath + '/v/' + event.code)
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe()
    this.dataStructure$ = undefined
  }

  fetchVariantsOnUnitChange() {
    this.subscriptions.add(
      this.ebUnitSelectorService
      .getUnitChanged()
      .subscribe((changed) => {
        if (changed) {
          this.fetchVariants(this.ebUnitSelectorService.getUnitValue(), true, this.currentSelectedValues)
          this.cd.detectChanges()
        }
      })
    )
  }

  /** invoke track event to matomo (parent page) **/
  private invokeMatomoEvents() {
    this.subscriptions.add(
    this.currentProduct$
        .pipe(filter(product=> this.isParent(product)))
        .subscribe(product => this.ebAnalyticsService.pushViewParentProductEvents(product))
    )
  }
}
